import { LoadingOutlined, LogoutOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  Button,
  Col,
  Form,
  Input,
  message,
  Modal,
  Row,
  Spin,
  Upload,
} from 'antd';
import { RcFile, UploadChangeParam, UploadFile } from 'antd/es/upload';
import 'cropperjs/dist/cropper.css';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Cropper, ReactCropperElement } from 'react-cropper';
import { S3PathEnum, UpdateUserInput, User } from '../../__generated__/graphql';
import { AppContext } from '../../AppContext';
import Edit_Icon from '../../assets/svg/editIcon.svg';
import { REGEX } from '../../common/constants';
import { fileUpload } from '../../common/utils';
import { messageContext } from '../../components/AppContextHolder';
import { customizeRequiredMark } from '../../components/CustomRequiredMark';
import useDetectBackButton from '../../components/useDetectBackButton';
import { AppActionType, AppContextType } from '../../types/appContext.type';
import { UPDATE_USER } from '../auth/graphql/mutations';
import { GET_CURRENT_USER } from '../auth/graphql/queries';
import '../profile/profile.less';
import { GET_USER_AVATAR_IMAGE } from './graphql/query';
import ChangeNumber from './Modal/ChangeNumber';
import UserActionConfirmationModal from './Modal/UserActionConfirmationModal';

function Profile() {
  const [profileForm] = Form.useForm();
  const {
    dispatch,
    state: { currentUser },
  } = useContext(AppContext) as AppContextType;
  const [isUserActionModalOpen, setUserActionModalOpen] =
    useState<boolean>(false);
  const [modalActionType, setModalActionType] = useState<
    'signout' | 'delete' | null
  >(null);
  const [isFormChanged, setIsFormChanged] = useState<boolean>(false);
  const [signedUrlKey, setSignedUrlKey] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState<boolean>(false);
  const cropperRef = useRef<ReactCropperElement>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentFile, setCurrentFile] = useState<UploadFile | null>(null);
  const [isChangeNumberModalOpen, setIsChangeNumberModalOpen] =
    useState<boolean>(false);
  const [isAvatarDeleted, setIsAvatarDeleted] = useState(false);
  const watchProfile = Form.useWatch('avatar', profileForm) || [];
  const [isBtnDisabled, setIsBtnDisabled] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const modal = useDetectBackButton(isDirty);

  const { loading: getCurrUserLoading } = useQuery(GET_CURRENT_USER, {
    onCompleted: ({ getLoggedInUserWeb }) => {
      profileForm.setFieldsValue({
        ...getLoggedInUserWeb,
        phoneNo: `${getLoggedInUserWeb?.phoneNo}`,
        avatar: getLoggedInUserWeb?.avatar
          ? [
              {
                name: 'image.png',
                status: 'done',
                url: getLoggedInUserWeb?.avatar ?? '',
              },
            ]
          : [],
      });
    },
    onError() {},
    fetchPolicy: 'network-only',
  });

  const [updateUser, { loading: updateLoading }] = useMutation(UPDATE_USER, {
    fetchPolicy: 'network-only',
    onCompleted: ({ updateUserWeb }) => {
      profileForm.setFieldsValue({
        ...updateUserWeb?.data,
        phoneNo: `${updateUserWeb?.data?.phoneNo}`,
        avatar: updateUserWeb?.data?.avatar
          ? [
              {
                name: 'image.png',
                status: 'done',
                url: updateUserWeb?.data?.avatar ?? '',
              },
            ]
          : [],
      });
      setIsBtnDisabled(true);
      dispatch({
        type: AppActionType.setCurrentUser,
        data: updateUserWeb?.data ?? {},
      });

      setIsFormChanged(isFormChanged);
    },
    onError() {},
  });

  const [uploadImgSignUrl] = useLazyQuery(GET_USER_AVATAR_IMAGE, {
    fetchPolicy: 'network-only',
    onError() {},
  });

  const handlePhoneNumberModalOpen = () => {
    setIsChangeNumberModalOpen(true);
  };

  const handlePhoneNumberModalClose = () => {
    setIsChangeNumberModalOpen(false);
  };

  const beforeUpload = (file: RcFile) => {
    const isImage = file.type.startsWith('image/');
    const ext = file.name.split('.')?.pop()?.toLowerCase();
    const allowed = ['jpg', 'jpeg', 'png'];

    if (!isImage || !allowed.includes(ext ?? '')) {
      messageContext.error(`${file.name} is not a valid image file.`);
      return Upload.LIST_IGNORE;
    }

    return false;
  };

  const onChangeImageUpload = async (info?: UploadChangeParam) => {
    setIsDirty(true);
    dispatch({
      type: AppActionType.setRouterPrompt,
      data: true,
    });
    if (!info) return;

    const file = info.fileList[0];

    if (file) {
      const { name, url, type } = file;
      const ext = name?.split('.')?.pop()?.toLowerCase();

      const isImage =
        type?.startsWith('image/') &&
        ['jpg', 'jpeg', 'png'].includes(ext ?? '');

      if (isImage && !url) {
        setCurrentFile(file);
        setShowCropper(true);
      } else {
        messageContext.destroy();
        messageContext.error(`${file?.name} is not a supported image file.`);
        profileForm.setFieldValue('avatar', []);
      }
    } else {
      setCurrentFile(null);
    }
  };

  const previewThumbnail = useMemo(
    () =>
      currentFile?.originFileObj
        ? URL.createObjectURL(currentFile.originFileObj)
        : '',
    [currentFile],
  );

  const handleCrop = async () => {
    const cropper = cropperRef.current?.cropper;

    if (cropper && previewThumbnail) {
      setLoading(true);
      try {
        const croppedCanvas = cropper.getCroppedCanvas();
        const croppedBlob: Blob = await new Promise((resolve, reject) =>
          croppedCanvas.toBlob((blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to crop the image.'));
            }
          }, currentFile?.type ?? ''),
        );

        const croppedFile = new File([croppedBlob], currentFile?.name ?? '', {
          type: currentFile?.type,
          lastModified: Date.now(),
        });

        if (croppedCanvas) {
          const croppedImage = croppedCanvas.toDataURL();
          profileForm.setFieldValue('avatar', [
            {
              url: croppedImage ?? '',
            },
          ]);
        }
        setCurrentFile((prev) =>
          prev
            ? {
                ...prev,
                originFileObj: croppedFile as RcFile,
              }
            : null,
        );
        setShowCropper(false);

        const data = await uploadImgSignUrl({
          variables: {
            data: {
              fileName: croppedFile.name,
              s3Path: S3PathEnum.UserAvatar,
            },
          },
        });

        const signedUrl = data?.data?.getImageSignedUrlWeb?.signedUrl;
        const signedUrlKey = data?.data?.getImageSignedUrlWeb?.key;
        setSignedUrlKey(data?.data?.getImageSignedUrlWeb?.key);
        if (signedUrl && signedUrlKey) {
          await fileUpload(signedUrl, croppedFile);
        }
        setShowCropper(false);
      } catch (_E) {
        //
      } finally {
        setLoading(loading);
      }
    }
  };

  const handleSubmit = async () => {
    try {
      const { firstName, lastName, email } = profileForm?.getFieldsValue();

      const updateData: UpdateUserInput = {
        email,
        firstName,
        lastName,
      };

      if (signedUrlKey) {
        updateData.avatar = signedUrlKey;
      } else if (isAvatarDeleted) {
        updateData.avatar = null;
      }

      const updateResponse = await updateUser({
        variables: {
          data: updateData,
        },
        fetchPolicy: 'network-only',
      });

      if (updateResponse) {
        dispatch({
          type: AppActionType.setRouterPrompt,
          data: false,
        });
        dispatch({
          type: AppActionType.setCurrentUser,
          data: updateResponse?.data?.updateUserWeb?.data as User,
        });

        setCurrentFile(null);
        setLoading(true);
        setIsAvatarDeleted(false);
      }
    } catch (error) {
      message.error('Error updating profile.');
    }
  };

  const updateMobileNumber = async (phoneNo?: string) => {
    await updateUser({
      variables: {
        data: {
          phoneNo: phoneNo,
        },
      },
    });
  };

  const handleDeleteAvatar = () => {
    profileForm.setFieldsValue({ avatar: [] });
    setSignedUrlKey(null);
    setCurrentFile(null);
    setIsAvatarDeleted(true);
  };

  const handleCancel = () => {
    setShowCropper(false);
    handleDeleteAvatar();
  };
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (showCropper && event.key === 'Enter') {
        event.preventDefault();
        handleCrop();
      }
    };

    if (showCropper) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showCropper]);

  if (getCurrUserLoading)
    return <Spin indicator={<LoadingOutlined spin />} className="loader" />;

  return (
    <div className="profile-container">
      {modal}
      <div className="heading body-base semibold">Profile Settings</div>

      <div className="profile-form-container">
        <Form
          layout="vertical"
          form={profileForm}
          onFinish={handleSubmit}
          requiredMark={customizeRequiredMark}
          onValuesChange={() => setIsBtnDisabled(false)}
        >
          <div className="profile-uploader">
            <Form.Item
              name="avatar"
              className="avatar-form-item"
              valuePropName="fileList"
              getValueFromEvent={(e) => {
                if (Array.isArray(e)) {
                  return e;
                }
                return e?.fileList;
              }}
            >
              <Upload
                accept="image/x-png, image/jpeg, image/jpg"
                maxCount={1}
                onChange={onChangeImageUpload}
                beforeUpload={beforeUpload}
                listType="picture-circle"
                showUploadList={{ showPreviewIcon: false }}
                className="upload-div"
                onRemove={handleDeleteAvatar}
              >
                {watchProfile?.length < 1 && (
                  <div className="upload-sub-div body-lg semibold">
                    {currentUser?.firstName?.[0] && currentUser?.lastName?.[0]
                      ? `${currentUser?.firstName[0]}${currentUser?.lastName[0]}`
                      : 'UN'}
                  </div>
                )}
                <img src={Edit_Icon} alt="edit" className="edit-icon pointer" />
              </Upload>
            </Form.Item>
          </div>
          <Row gutter={16}>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Form.Item
                label={<span className="body-meta medium">First Name</span>}
                name="firstName"
                required
                rules={[
                  { required: true, message: 'First Name is required' },
                  {
                    pattern: REGEX.NAME,
                    message: 'First Name can only contain alphabets',
                  },
                  {
                    validator: (_, value) => {
                      if (value && value.trimStart() !== value) {
                        return Promise.reject(
                          new Error('Only spaces are not allowed!'),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  type="text"
                  placeholder="Enter your First Name"
                  onChange={() => {
                    setIsDirty(true);
                    dispatch({
                      type: AppActionType.setRouterPrompt,
                      data: true,
                    });
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Form.Item
                label={<span className="body-meta medium">Last Name</span>}
                name="lastName"
                required
                rules={[
                  { required: true, message: 'Last Name is required' },
                  {
                    pattern: REGEX.NAME,
                    message: 'Last Name can only contain alphabets',
                  },
                  {
                    validator: (_, value) => {
                      if (value && value.trimStart() !== value) {
                        return Promise.reject(
                          new Error('Only spaces are not allowed!'),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  type="text"
                  placeholder="Enter your Last Name"
                  onChange={() => {
                    setIsDirty(true);
                    dispatch({
                      type: AppActionType.setRouterPrompt,
                      data: true,
                    });
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label={<span className="body-meta medium">Phone Number</span>}
            name="phoneNo"
            required
          >
            <Input
              className="change-number-input"
              type="text"
              readOnly
              placeholder="Enter your Phone number"
              suffix={
                <div onClick={(e) => e.stopPropagation()}>
                  <Button
                    type="text"
                    onClick={handlePhoneNumberModalOpen}
                    className="change-button"
                  >
                    Change
                  </Button>
                </div>
              }
            />
          </Form.Item>

          <Form.Item
            label={<span className="body-meta medium">Email Address</span>}
            name="email"
            rules={[
              {
                validator: (_, value) => {
                  if (value && value.trimStart() !== value) {
                    return Promise.reject(
                      new Error('Only spaces are not allowed!'),
                    );
                  }
                  if (value && !REGEX.EMAIL.test(value)) {
                    return Promise.reject(
                      new Error('Please enter a valid email Address!'),
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              type="text"
              onChange={() => {
                setIsDirty(true);
                dispatch({
                  type: AppActionType.setRouterPrompt,
                  data: true,
                });
              }}
              placeholder="example@gmail.com"
              className="email-input"
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              className="save-changes-button body-meta medium "
              loading={updateLoading}
              disabled={isBtnDisabled}
            >
              Save Changes
            </Button>
          </Form.Item>

          <div className="signout">
            <Button
              className="sign-out-btn"
              type="text"
              onClick={() => {
                setModalActionType('signout');
                setUserActionModalOpen(true);
              }}
            >
              Sign Out <LogoutOutlined />
            </Button>
          </div>

          <div className="delete-account">
            <Button
              className="delete-btn"
              type="text"
              onClick={() => {
                setModalActionType('delete');
                setUserActionModalOpen(true);
              }}
            >
              Delete Account
            </Button>
          </div>
        </Form>
        <Modal
          width={800}
          title="Crop Image"
          closable={false}
          open={showCropper}
          onOk={handleCrop}
          footer={[
            <Button key="cancel" onClick={handleCancel} type="default">
              Cancel
            </Button>,
            <Button key="crop" onClick={handleCrop} type="primary">
              Save
            </Button>,
          ]}
        >
          <Cropper
            className="cropper"
            src={previewThumbnail}
            aspectRatio={NaN}
            guides={true}
            ref={cropperRef}
            viewMode={1}
            background={false}
            autoCropArea={1}
            responsive={true}
            checkOrientation={false}
          />
        </Modal>
        {isChangeNumberModalOpen && (
          <ChangeNumber
            visible={isChangeNumberModalOpen}
            onClose={handlePhoneNumberModalClose}
            footer={null}
            updateMobileNumber={updateMobileNumber}
          />
        )}
        {modalActionType && (
          <UserActionConfirmationModal
            visible={isUserActionModalOpen}
            onClose={() => {
              setUserActionModalOpen(false);
              setModalActionType(null);
            }}
            title={modalActionType === 'delete' ? 'Delete Account' : 'Sign Out'}
            content={
              modalActionType === 'delete'
                ? 'Are you sure you would like to delete this profile? Your account and reports will be permanently deleted.'
                : 'Are you sure you want to sign out?'
            }
            type={modalActionType}
          />
        )}
      </div>
    </div>
  );
}
export default Profile;
